import React, { useState } from 'react';
import { BookOpen, Database, GraduationCap, Plus, ShieldCheck, SlidersHorizontal, Users } from 'lucide-react';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import StatCard from '../components/Dashboard/StatCard';
import { StatCardSkeleton, ErrorState } from '../components/ui/Skeleton';
import PageHeader from '../components/ui/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, STALE } from '../hooks/useQuery';
import { supabase } from '../lib/supabaseClient';

const AdminDashboardPage = () => {
  const { user } = useAuth();
  const [word, setWord] = useState({ german: '', albanian: '', level: 'C1', chapter: '', module: '' });
  const [wordMessage, setWordMessage] = useState('');

  const {
    data: stats,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery(
    'admin_stats',
    null,
    async () => {
      const [usersResult, lessonsResult] = await Promise.all([
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
        supabase.from('lessons').select('id', { count: 'exact', head: true }),
      ]);
      if (usersResult.error) console.error('Admin users stats error:', usersResult.error);
      if (lessonsResult.error) console.error('Admin lessons stats error:', lessonsResult.error);
      return {
        totalUsers: usersResult.count || 0,
        totalLessons: lessonsResult.count || 0,
      };
    },
    { staleTime: STALE.short, enabled: !!user }
  );

  const addVocabulary = async (event) => {
    event.preventDefault();
    setWordMessage('');
    const { error } = await supabase.from('admin_vocabulary').insert({
      ...word,
      created_by: user.id,
    });
    if (error) {
      console.error('Vocabulary insert error:', error);
      setWordMessage('Fjala nuk u ruajt. Ekzekuto skemën e re në Supabase.');
      return;
    }
    setWord({ german: '', albanian: '', level: 'C1', chapter: '', module: '' });
    setWordMessage('Fjala u shtua me sukses.');
  };

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-1 pb-10 font-sans sm:px-3">
        <PageHeader
          icon={ShieldCheck}
          badge="Administrimi"
          title="Menaxhimi i Platformës"
          description="Kontrollo përmbajtjen, fjalorin dhe progresin e studentëve."
        >
          <div className="flex gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-xl bg-zinc-950 px-4 py-2 text-xs font-bold text-white hover:bg-[#DD0000] transition"
            >
              <Plus size={14} /> Shto Mësim
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2 text-xs font-bold text-zinc-700 hover:bg-zinc-50 transition"
            >
              <SlidersHorizontal size={14} /> Cilësime
            </button>
          </div>
        </PageHeader>

        {queryError && (
          <ErrorState
            message="Statistikat e adminit nuk mund të ngarkoheshin."
            onRetry={refetch}
          />
        )}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {isLoading ? (
            <>
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
            </>
          ) : (
            <>
              <StatCard label="Studentë" value={stats?.totalUsers ?? 0} icon={Users} />
              <StatCard label="Leksione" value={stats?.totalLessons ?? 0} icon={BookOpen} />
              <StatCard label="Niveli" value="B2" icon={GraduationCap} />
              <StatCard label="Platforma" value="Aktive" icon={Database} />
            </>
          )}
        </div>

        <div className="overflow-hidden rounded-2xl border border-zinc-200/80 bg-white p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="rounded-xl bg-emerald-50 p-3 text-emerald-600">
              <Database size={22} />
            </div>
            <div>
              <h2 className="font-bold text-zinc-900">Paneli admin është aktiv</h2>
              <p className="mt-1 text-sm leading-relaxed text-zinc-500">
                Statistikat lexohen direkt nga Supabase. Përdor menunë anësore për t&apos;u kthyer te mësimi.
              </p>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-zinc-200/80 bg-white p-6 shadow-sm space-y-4">
          <div>
            <h2 className="text-base font-bold text-zinc-900">Shto Fjalor të Ri</h2>
            <p className="mt-1 text-xs text-zinc-500">Shto fjalë për C1 ose nivele të tjera.</p>
          </div>
          <form onSubmit={addVocabulary} className="grid gap-3 md:grid-cols-2">
            <input
              required
              value={word.german}
              onChange={(e) => setWord({ ...word, german: e.target.value })}
              placeholder="Fjala gjermanisht"
              className="h-10 rounded-xl border border-zinc-200 px-3 text-xs focus:border-[#DD0000] focus:outline-none"
            />
            <input
              required
              value={word.albanian}
              onChange={(e) => setWord({ ...word, albanian: e.target.value })}
              placeholder="Përkthimi shqip"
              className="h-10 rounded-xl border border-zinc-200 px-3 text-xs focus:border-[#DD0000] focus:outline-none"
            />
            <select
              value={word.level}
              onChange={(e) => setWord({ ...word, level: e.target.value })}
              className="h-10 rounded-xl border border-zinc-200 bg-white px-3 text-xs focus:border-[#DD0000] focus:outline-none cursor-pointer"
            >
              <option>A1</option>
              <option>A2</option>
              <option>B1</option>
              <option>B2</option>
              <option>C1</option>
              <option>C2</option>
            </select>
            <input
              value={word.chapter}
              onChange={(e) => setWord({ ...word, chapter: e.target.value })}
              placeholder="Kapitel"
              className="h-10 rounded-xl border border-zinc-200 px-3 text-xs focus:border-[#DD0000] focus:outline-none"
            />
            <input
              value={word.module}
              onChange={(e) => setWord({ ...word, module: e.target.value })}
              placeholder="Modul"
              className="h-10 rounded-xl border border-zinc-200 px-3 text-xs focus:border-[#DD0000] focus:outline-none"
            />
            <button
              type="submit"
              className="h-10 rounded-xl bg-zinc-950 font-bold text-xs text-white hover:bg-[#DD0000] transition"
            >
              Ruaj fjalën
            </button>
          </form>
          {wordMessage && <p className="text-xs font-semibold text-emerald-700">{wordMessage}</p>}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboardPage;
